import configs from "../configs";
import { getAuth0Token } from "../ui/utils";

/**
 * AssetOptimizationService - Service for optimizing GLTF/GLB assets using the Persistence API with queue system
 *
 * This service provides methods to:
 * - Optimize assets with predefined optimization presets (now uses async queue system)
 * - Poll job status for long-running optimizations
 * - Confirm optimization and replace original file
 * - Cancel optimization and cleanup temporary files
 *
 * Available presets:
 * - "maximum" - Maximum compression (textures: 256px, mesh simplification: 40%)
 * - "balanced" - Balanced quality/size (textures: 512px, mesh simplification: 70%)
 * - "quality" - High quality (textures: 1024px, mesh simplification: 90%)
 * - "minimal" - Minimal optimization (textures: 2048px, no mesh simplification)
 *
 * NEW: Queue-based optimization flow:
 * 1. POST /optimize -> 202 Accepted + jobId (immediate response)
 * 2. GET /optimize/jobs/:jobId -> poll status until completed
 * 3. When completed, download the optimized file
 */
export default class AssetOptimizationService {
  constructor() {
    this.baseUrl = `${configs.PERSISTENCE_API_URL}/assets`;
  }

  /**
   * Get authentication headers for API requests
   * @returns {Promise<Object>} Headers object with Authorization token
   */
  async getHeaders() {
    const token = await getAuth0Token();
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      Accept: "application/json"
    };
  }

  /**
   * Start optimization of a GLTF/GLB asset (queue-based)
   *
   * @param {string|number} assetId - Asset ID (integer) or UUID (string)
   * @param {string} preset - Optimization preset name ("maximum", "balanced", "quality", "minimal")
   * @returns {Promise<Object>} Object containing jobId if async, or blob/url if completed immediately
   * @throws {Error} If optimization request fails
   */
  async optimize(assetId, preset = "balanced") {
    // Validate preset
    const validPresets = ["maximum", "balanced", "quality", "minimal"];
    if (!validPresets.includes(preset)) {
      throw new Error(`Invalid preset: ${preset}. Valid presets are: ${validPresets.join(", ")}`);
    }

    const headers = await this.getHeaders();
    const url = `${this.baseUrl}/${assetId}/optimize`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ preset })
      });

      if (response.status === 202) {
        // Async optimization started - return jobId for polling
        const result = await response.json();
        return {
          status: "processing",
          jobId: result.jobId,
          message: result.message,
          preset
        };
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Optimization failed with status ${response.status}`;
        throw new Error(errorMessage);
      }

      // Optimization completed immediately (200 OK) - get the optimized file as a blob
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      return {
        status: "completed",
        blob,
        url: blobUrl,
        size: blob.size,
        preset,
        contentType: response.headers.get("Content-Type") || "model/gltf-binary"
      };
    } catch (error) {
      // Re-throw with more context if it's not already an Error
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Optimization failed: ${error.message || error}`);
    }
  }

  /**
   * Poll the status of an optimization job
   *
   * @param {string} jobId - Job ID returned from optimize() call
   * @returns {Promise<Object>} Job status object with state, result, and failedReason
   * @throws {Error} If status check fails
   */
  async getJobStatus(jobId) {
    if (!jobId || typeof jobId !== "string") {
      throw new Error("Valid jobId is required");
    }

    const headers = await this.getHeaders();
    const url = `${this.baseUrl}/optimize/jobs/${jobId}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Status check failed with status ${response.status}`;
        throw new Error(errorMessage);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Status check failed: ${error.message || error}`);
    }
  }

  /**
   * Poll job status and get temporary Azure URL when completed
   *
   * @param {string} jobId - Job ID returned from optimize() call
   * @param {string} preset - The preset used for optimization
   * @param {Object} options - Polling options
   * @param {number} options.maxAttempts - Maximum polling attempts (default: 150, ~5 minutes)
   * @param {number} options.interval - Polling interval in ms (default: 2000)
   * @param {AbortController} options.abortController - AbortController to cancel polling
   * @returns {Promise<Object>} Object containing tempAzureUrl, size, and preset
   * @throws {Error} If polling times out, job fails, or is aborted
   */
  async pollForCompletion(jobId, preset = "balanced", options = {}) {
    const { maxAttempts = 150, interval = 2000, abortController } = options;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Check if polling was cancelled
      if (abortController && abortController.signal.aborted) {
        throw new Error("Polling cancelled by user");
      }

      try {
        const headers = await this.getHeaders();
        const url = `${this.baseUrl}/optimize/jobs/${jobId}`;

        const response = await fetch(url, {
          method: "GET",
          headers,
          signal: abortController ? abortController.signal : undefined
        });

        // Parse JSON status response (backend now always returns JSON)
        const status = await response.json();

        // Handle HTTP errors
        if (!response.ok) {
          if (response.status === 404 && status.error === "Job not found") {
            throw new Error(`Job not found. It may have been cleaned up.`);
          } else {
            throw new Error(`Server error: ${status.error || response.statusText}`);
          }
        }

        if (status.state === "completed" && status.result && status.result.success) {
          // Job completed successfully - return the temporary Azure URL and metadata
          console.log(`[PollJob] Job ${jobId} completed - returning temp Azure URL for confirmation`);
          return {
            tempAzureUrl: status.result.tempAzureUrl,
            size: status.result.optimizedSize,
            preset,
            contentType: status.result.contentType || "model/gltf-binary"
          };
        }

        if (status.state === "failed") {
          throw new Error(`Optimization failed: ${status.failedReason || "Unknown error"}`);
        }

        // Job still processing, wait before next poll
        if (attempt < maxAttempts - 1) {
          // Check if polling was cancelled before waiting
          if (abortController && abortController.signal.aborted) {
            throw new Error("Polling cancelled by user");
          }
          await new Promise(resolve => setTimeout(resolve, interval));
        }
      } catch (error) {
        // Handle specific errors that shouldn't be retried
        if (error.message.includes("Job not found")) {
          throw error; // Don't retry for job not found errors
        }

        // Optimization failed errors should fail immediately without retry
        if (error.message.includes("Optimization failed:")) {
          throw error; // Fail immediately for optimization errors
        }

        // Polling cancelled errors should fail immediately without retry
        if (error.message.includes("Polling cancelled by user")) {
          throw error; // Fail immediately for cancellation
        }

        // Network or other error, retry
        if (attempt === maxAttempts - 1) {
          throw new Error(`Failed to check job status after ${maxAttempts} attempts: ${error.message}`);
        }
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }

    throw new Error(`Job polling timed out after ${maxAttempts} attempts`);
  }

  /**
   * Confirm optimization - move file from temp location and update database
   *
   * This method finalizes the optimization by:
   * - Getting temp file info from the completed job
   * - Moving the optimized file from temp to final Azure location
   * - Updating the database with new paths and sizes
   * - Creating copies if requested
   * - Adding copied assets to collections if specified
   *
   * @param {string} jobId - The job ID returned from the optimize call
   * @param {string|number} assetId - Asset ID (integer) or UUID (string) to confirm
   * @param {string} preset - The preset name used for optimization (for validation only)
   * @param {number} optimizedFileSize - File size (kept for API compatibility)
   * @param {string} contentType - Content type (kept for API compatibility)
   * @param {boolean} createCopy - Whether to create a copy instead of replacing
   * @param {string|number|null} collectionId - Collection ID to add the copied asset to (only used when createCopy=true)
   * @returns {Promise<Object>} Updated or new asset object
   * @throws {Error} If confirmation fails
   */
  async confirm(
    jobId,
    assetId,
    preset,
    optimizedFileSize,
    contentType = "model/gltf-binary",
    createCopy = false,
    collectionId = null
  ) {
    // Basic validation for API consistency
    const validPresets = ["maximum", "balanced", "quality", "minimal"];
    if (!validPresets.includes(preset)) {
      throw new Error(`Invalid preset: ${preset}. Valid presets are: ${validPresets.join(", ")}`);
    }

    if (!jobId) {
      throw new Error("jobId is required for confirmation");
    }

    if (!assetId) {
      throw new Error("assetId is required for confirmation");
    }

    const headers = await this.getHeaders();
    const url = `${this.baseUrl}/optimize/jobs/${jobId}/confirm`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          preset,
          size: optimizedFileSize || 0, // Kept for API compatibility
          contentType,
          createCopy,
          assetId: assetId, // Required for the backend to know which asset to update
          ...(collectionId && { collectionId }) // Include collectionId if provided
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Confirmation failed with status ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      const result = await response.json();
      return result.data; // Current asset object
    } catch (error) {
      // Re-throw with more context if it's not already an Error
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Confirmation failed: ${error.message || error}`);
    }
  }

  /**
   * Cancel optimization - remove job from queue and cleanup temp files
   *
   * @param {string} jobId - Job ID returned from the optimize call
   * @returns {Promise<Object>} Result object with success status and cleanup info
   * @throws {Error} If cancellation fails
   */
  async cancel(jobId) {
    const headers = await this.getHeaders();
    const url = `${this.baseUrl}/optimize/jobs/${jobId}/cancel`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Cancellation failed with status ${response.status}`;
        throw new Error(errorMessage);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      // Re-throw with more context if it's not already an Error
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Cancellation failed: ${error.message || error}`);
    }
  }

  /**
   * Cleanup blob URL to prevent memory leaks
   *
   * @param {string} blobUrl - The blob URL to revoke
   */
  revokeBlobUrl(blobUrl) {
    if (blobUrl && typeof blobUrl === "string" && blobUrl.startsWith("blob:")) {
      URL.revokeObjectURL(blobUrl);
    }
  }
}
